import { EntityAdapter, Update } from "@reduxjs/toolkit";
import { aeTitleRuleAdapter, backendValidationErrorAdapter, ContouringSliceState, customizationBaseAdapter, customizationOutputAdapter, dicomAttributeRuleAdapter, dicomRuleAdapter, globalRoiCustomizationAdapter, modelAdapter, outputMetadataAdapter, roiCustomizationAdapter } from "../../contouring/contouringSlice";
import GenericSliceWrapper, { RemovedOutputEntities } from "./genericSliceWrapper";
import { ContouringCustomizationEntities, ContouringCustomizationOutput, ContouringRoi, GlobalContouringRoi } from "../../contouring/contouring-types";

export class ContourSliceWrapper extends GenericSliceWrapper<ContouringSliceState> {

    outputAdapter: EntityAdapter<ContouringCustomizationOutput, string>;
    roiAdapter: EntityAdapter<ContouringRoi, string>;
    globalRoiAdapter: EntityAdapter<GlobalContouringRoi, string>

    constructor() {
        super(
            modelAdapter,
            customizationBaseAdapter,
            outputMetadataAdapter,
            aeTitleRuleAdapter,
            dicomRuleAdapter,
            dicomAttributeRuleAdapter,
            backendValidationErrorAdapter,
        )
        this.outputAdapter = customizationOutputAdapter;
        this.roiAdapter = roiCustomizationAdapter;
        this.globalRoiAdapter = globalRoiCustomizationAdapter;
    }

    protected findOutput(state: ContouringSliceState, outputId: string): ContouringCustomizationOutput | undefined {
        return state.contourOutputs.entities[outputId];
    }

    protected updateOutput(state: ContouringSliceState, update: Update<ContouringCustomizationOutput, string>): void {
        this.outputAdapter.updateOne(state.contourOutputs, update);
    }

    /** Collects which store entities need to be removed and updated when a customization output is removed. */
    public collectCustomizationOutputEntitiesFromStoreForRemoval(state: ContouringSliceState, output: ContouringCustomizationOutput, customizationBaseId: string): RemovedOutputEntities {

        const globalRoiChanges: Update<GlobalContouringRoi, string>[] = [];
        const roiIdsToBeRemoved: string[] = [];
        const metadataIdsToBeRemoved: string[] = [];


        metadataIdsToBeRemoved.push(...output.metadata);

        for (const roiId of output.rois) {
            const roi = state.roiCustomizations.entities[roiId];
            if (roi === undefined) {
                throw new Error(`Could not retrieve roi ${roiId} for output ${output.id} in customization base ${customizationBaseId}`);
            }

            roiIdsToBeRemoved.push(roiId);

            const globalRoi = roi.globalRoiId ?
                state.globalRoiCustomizations.entities[roi.globalRoiId] :
                Object.values(state.globalRoiCustomizations.entities).find(r => r?.excludedRois.includes(roiId));
            if (globalRoi) {
                globalRoiChanges.push({
                    id: globalRoi.id, changes: {
                        excludedRois: globalRoi.excludedRois.filter(r => r !== roiId),
                        coveredRois: globalRoi.coveredRois.filter(r => r !== roiId),
                    }
                });
            }
        }

        return {
            globalRoiChanges,
            roiIdsToBeRemoved,
            metadataIdsToBeRemoved,
            allRemovedIds: [...roiIdsToBeRemoved, ...metadataIdsToBeRemoved],
        };
    };

    protected removeOutputRelatedItems(state: ContouringSliceState, outputIds: string[], items: RemovedOutputEntities) {
        const { roiIdsToBeRemoved, metadataIdsToBeRemoved, globalRoiChanges } = items;

        // update global rois first
        if (globalRoiChanges) { this.globalRoiAdapter.updateMany(state.globalRoiCustomizations, globalRoiChanges); }

        if (roiIdsToBeRemoved) { this.roiAdapter.removeMany(state.roiCustomizations, roiIdsToBeRemoved); }
        if (metadataIdsToBeRemoved) { this.outputMetadataAdapter.removeMany(state.outputMetadata, metadataIdsToBeRemoved); }
        this.outputAdapter.removeMany(state.contourOutputs, outputIds);
    }

    protected setOutputRelatedItemsAsModified(state: ContouringSliceState, entitiesToUpdate: Partial<ContouringCustomizationEntities>) {
        const roiIds = entitiesToUpdate.contouringRois?.map(id => id.id);
        const globalRoiIds = entitiesToUpdate.contouringGlobalRois?.map(id => id.id);
        const customizationOutputsIds = entitiesToUpdate.contouringOutputs?.map(id => id.id);

        if (roiIds && roiIds.length > 0) {
            this.roiAdapter.updateMany(
                state.roiCustomizations,
                roiIds.map(roiId => ({ id: roiId, changes: { isModified: true } }))
            );
        }

        if (globalRoiIds && globalRoiIds.length > 0) {
            this.globalRoiAdapter.updateMany(
                state.globalRoiCustomizations,
                globalRoiIds.map(globalRoiId => ({ id: globalRoiId, changes: { isModified: true } }))
            );
        }

        if (customizationOutputsIds && customizationOutputsIds.length > 0) {
            this.outputAdapter.updateMany(
                state.contourOutputs,
                customizationOutputsIds.map(customizationOutputId => ({ id: customizationOutputId, changes: { isModified: true } }))
            );
        }
    }


}
